import React, { useState } from "react";
import { apiPost } from "./restUtil.service";

interface User {
    [key: string]: string | number;
}

interface Chat {
    [key: string]: string | number;
}

interface Conversation {
    [key: string]: string | number;
}

interface Session {
    [key: string]: string | number;
}

interface ApiResponse {
    user: User;
    chat: Chat;
    conversations: Conversation[];
    sessions: Session[];
}

const Home: React.FC = () => {
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [userInfo, setUserInfo] = useState<User>({});
    const [error, setError] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [data, setData] = useState<ApiResponse | null>(null);

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const form = event.currentTarget;
        const formData = new FormData(form);

        const email = formData.get("email") as string;
        const mobile = formData.get("mobile") as string;
        const stripeId = formData.get("stripeId") as string;

        if (!email && !mobile && !stripeId) {
            setError("Please fill at least one of the field.");
            return;
        }

        try {
            setIsLoading(true);
            const response = await apiPost<{ email: string, mobile: string, stripeId: string }, ApiResponse>("/admin/search-user", { email, mobile, stripeId });
            setData(response);
        } catch (error) {
            console.error("Error:", error);
        } finally {
            setIsLoading(false);
        }
    };

    function getDeviceFromUserAgent(userAgent: string) {
        const deviceRegex = /\(([^)]+)\)/; // Matches content inside parentheses
        const browserRegex = /^[^\s]+/;   // Extracts the first part of the user-agent string for the browser

        const deviceMatch = userAgent.match(deviceRegex);
        const browserMatch = userAgent.match(browserRegex);

        if (deviceMatch && browserMatch) {
            const deviceInfo = deviceMatch[1];
            const browserInfo = browserMatch[0];

            // Extracting the device and OS from the device information
            const [device, os] = deviceInfo.split(";").map(info => info.trim());

            return `${device || "Unknown Device"} | ${os || "Unknown OS"} | ${browserInfo || "Unknown Browser"}`;
        };
        return `${"Unknown Device"} | ${"Unknown OS"} | ${"Unknown Browser"}`;
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setUserInfo((prev) => ({ ...prev, [name]: value }));
    };

    const handleSave = async () => {
        try {
            setIsLoading(true);
            const response = await apiPost<{ userId: string, type: string, value: string }, { statusCode: 201, message: string }>("/admin/update-user", {
                userId: data?.user?._id?.toString() || "",
                type: userInfo.email !== data?.user?.email ? 'email' : 'mobile',
                value: userInfo.email !== data?.user?.email ? userInfo.email?.toString() : userInfo.mobileNumber?.toString()
            });
            if (response?.statusCode === 201) {
                const newData = await apiPost<{ stripeId?: string }, ApiResponse>("/admin/search-user", { stripeId: data?.user?.stripeCustomerId?.toString() });
                setData(newData);
                setError("");
                setUserInfo({});
                alert(response?.message);
                setIsEditing(false);
            }

        } catch (error: any) {
            setError(`Failed to update user information. ${error?.message}. Please try again.`);
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <div className="container">
            {isLoading && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        backgroundColor: "rgba(255, 255, 255, 0.13)",
                        backdropFilter: "blur(2px)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 9999,
                    }}
                >
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            )}
            {/* Form */}
            <form onSubmit={handleSubmit} className="mt-3">
                <div className="row">
                    <div className="mb-3 col-sm-4">
                        <label htmlFor="email" className="form-label">Email</label>
                        <input type="email" id="email" name="email" className="form-control" />
                    </div>
                    <div className="mb-3 col-sm-4">
                        <label htmlFor="mobile" className="form-label">Mobile</label>
                        <input type="tel" id="mobile" name="mobile" className="form-control" />
                    </div>
                    <div className="mb-3 col-sm-4">
                        <label htmlFor="stripeId" className="form-label">Stripe ID</label>
                        <input type="text" id="stripeId" name="stripeId" className="form-control" />
                    </div>
                    <div className="mb-3 col-sm-4">
                        <button type="submit" className="btn btn-primary">Submit</button>
                    </div>
                </div>
                <div className="row">
                    <div className="col-sm-12 text-error mb-3" style={{ display: !error ? "none" : "block" }}>
                        <p className="text-danger">{error}</p>
                    </div>
                </div>
            </form>
            {/* Render Data */}
            {
                data?.user && (
                    <div className="row">
                        {/* User Info Section */}
                        <div className="col-sm-4">
                            <div className="card mb-4">
                                <div className="card-header d-flex justify-content-between align-items-center">
                                    <span>User Information : {(data?.user?.isDeleted || data?.user?.deletedAt ? 'Deleted' : 'Active')}</span>
                                    <button className="btn btn-sm btn-primary" onClick={() => {
                                        if (isEditing) {
                                            handleSave();
                                        } else {
                                            setIsEditing((prev) => !prev);
                                            setUserInfo({
                                                email: data?.user?.email,
                                                mobileNumber: data?.user?.mobileNumber,
                                            });
                                        }
                                    }}>
                                        {isEditing ? "Save" : "Edit"}
                                    </button>
                                </div>
                                <div className="card-body">
                                    <div className="mb-3 d-flex align-items-center">
                                        <label className="me-2" style={{ width: "150px" }}><strong>Stripe ID:</strong></label>
                                        <input
                                            disabled
                                            readOnly
                                            type="text"
                                            className="form-control flex-grow-1 bg-none"
                                            name="stripeCustomerId"
                                            value={data?.user?.stripeCustomerId}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                    <div className="mb-3 d-flex align-items-center">
                                        <label className="me-2" style={{ width: "150px" }}><strong>Email:</strong></label>
                                        <input
                                            disabled={!isEditing}
                                            type="email"
                                            className="form-control flex-grow-1"
                                            name="email"
                                            value={userInfo?.email || data?.user?.email}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                    <div className="mb-3 d-flex align-items-center">
                                        <label className="me-2" style={{ width: "150px" }}><strong>Mobile:</strong></label>
                                        <input
                                            disabled={!isEditing}
                                            type="text"
                                            className="form-control flex-grow-1"
                                            name="mobileNumber"
                                            value={userInfo?.mobileNumber || data?.user?.mobileNumber}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-sm-8">
                            <div className="card mb-4">
                                <div className="card-header">Chats: {data?.chat?.chatName}</div>
                                <div className="card-body">
                                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                                        <p><strong>Started At:</strong> {data?.chat?.createdAt}</p>
                                        <p><strong>Last Updated:</strong> {data?.chat?.updatedAt}</p>
                                    </div>
                                    <p><strong>Device: </strong>{data?.chat?.device || 'Information not available'}</p>
                                </div>
                                <table className="table table-bordered">
                                    <thead>
                                        <tr>
                                            <th>Questions Asked</th>
                                            <th>Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {
                                            data?.conversations?.map((item: any, index) => (
                                                <tr key={index}>
                                                    <td>{item?.question}</td>
                                                    <td>{item?.createdAt}</td>
                                                </tr>
                                            ))
                                        }
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        {/* Session Info Section */}
                        <div className="col-sm-12">
                            <div className="card">
                                <div className="card-header">Sessions</div>
                                <div className="card-body p-0">
                                    {data?.sessions && data?.sessions?.length > 0 ? (
                                        <table className="table table-bordered">
                                            <thead>
                                                <tr>
                                                    <th>Device</th>
                                                    <th>Start Time</th>
                                                    <th>End Time</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {data?.sessions?.map((item: any, index: number) => (
                                                    <tr key={index}>
                                                        <td>{getDeviceFromUserAgent(item?.platform)}</td>
                                                        <td>{item?.startTime || "N/A"}</td>
                                                        <td>{item?.endTime || "N/A"}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    ) : (
                                        <p>No sessions available.</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }
        </div>
    );
};

export default Home;
